import { getPref, setPref } from '../persistentPrefs';
import {
  chooseSkillClass as chooseSkillClassApi,
  spendSkillPoint as spendSkillPointApi,
  purchaseSkillResetServer,
} from './profileApi';
import { getClassNodes, getNode, isClassAvailable } from './classes';
import { isFreePlay } from '../freePlay';

const STORAGE_KEY = 'vh-skills-v2';

// Persistent state shape: { classKey: 'wildheart' | 'alchemist' | null, pointsSpent: [nodeKey, ...] }
//
// The server is the source of truth. It validates every spend against
// the class tree and stores the blob on the player row. The local cache
// exists so renders are sync and fast; it is seeded from
// arenaProfile.skills on profile load and updated alongside every
// successful server-side mutation.

export function normalizeSkillState(raw) {
  if (!raw || typeof raw !== 'object') return { classKey: null, pointsSpent: [] };
  const classKey = typeof raw.classKey === 'string' && isClassAvailable(raw.classKey) ? raw.classKey : null;
  const valid = new Set(getClassNodes(classKey).map((n) => n.key));
  const pointsSpent = Array.isArray(raw.pointsSpent) ? raw.pointsSpent.filter((k) => valid.has(k)) : [];
  return { classKey, pointsSpent };
}

// Free play has no classes: every reader (board overlays, the Oracle's
// prediction, the Alchemist's chip) sees a player without one. Read-only,
// so the cached server state is left as it is.
export function getSkillState() {
  if (isFreePlay()) return normalizeSkillState(null);
  return normalizeSkillState(getPref(STORAGE_KEY, null));
}

function saveState(state) {
  setPref(STORAGE_KEY, normalizeSkillState(state));
}

// Replace the local cache with whatever the server returned. Called
// from app.jsx whenever loadArenaProfile resolves so the cached state
// and the server stay in lockstep.
export function seedSkillState(skills) {
  if (skills && typeof skills === 'object') saveState(skills);
}

// Gold price of changing class. Mirrors SKILL_RESET_COST in
// valkenhall-server/src/utils/skills.js, which is the value actually
// charged. This copy only drives labels and the local affordability check.
export const SKILL_RESET_COST = 5000;

// Skill points are earned every two player levels. With a level cap of
// 30 that is 15 points. Wildheart has 20 nodes with two either-or
// forks; Alchemist has 25 nodes across its potion and humour branches. A player can never hold the whole tree, so every point is a
// real decision. Reversing them is deliberately expensive rather than
// impossible: purchaseSkillReset wipes the tree and the class for
// SKILL_RESET_COST gold.
export const TOTAL_SKILL_POINTS = 15;

// Available skill points come from the server-side granted_skill_points
// column, surfaced on the arenaProfile as `grantedSkillPoints` or
// `progression.grantedSkillPoints`. The Progression screen is the sole
// path that advances this counter.
export function getAvailableSkillPoints(profileOrGrantedCount) {
  const granted = typeof profileOrGrantedCount === 'number'
    ? profileOrGrantedCount
    : profileOrGrantedCount?.progression?.grantedSkillPoints
      ?? profileOrGrantedCount?.grantedSkillPoints
      ?? 0;
  return Math.min(Math.max(0, Math.floor(granted)), TOTAL_SKILL_POINTS);
}

export function countSpent(state) {
  return state?.pointsSpent?.length || 0;
}

function prereqMet(node, spent) {
  return !node.requires || node.requires.some((k) => spent.includes(k));
}

// Node status inside the player's class tree:
//   purchased  the point is spent
//   excluded   the other side of an either-or fork is purchased
//   locked     no prerequisite purchased yet, or below the level gate
//   available  can be bought now
export function getNodeStatus(nodeKey, state, playerLevel = Infinity) {
  const node = getNode(state?.classKey, nodeKey);
  if (!node) return 'locked';
  const spent = state.pointsSpent || [];
  if (spent.includes(nodeKey)) return 'purchased';
  if ((node.excludes || []).some((k) => spent.includes(k))) return 'excluded';
  if (!prereqMet(node, spent)) return 'locked';
  if (playerLevel < (node.minLevel || 0)) return 'locked';
  return 'available';
}

// Why a node cannot be bought right now, for the tooltip. Null when it can.
export function getNodeBlocker(nodeKey, state, playerLevel = Infinity) {
  const node = getNode(state?.classKey, nodeKey);
  if (!node) return null;
  const spent = state.pointsSpent || [];
  const status = getNodeStatus(nodeKey, state, playerLevel);
  if (status === 'excluded') {
    return { kind: 'excluded', nodeKey: (node.excludes || []).find((k) => spent.includes(k)) };
  }
  if (status === 'locked') {
    if (!prereqMet(node, spent)) return { kind: 'requires', nodeKeys: node.requires };
    return { kind: 'level', level: node.minLevel };
  }
  return null;
}

// Optimistically update the local cache and push the choice to the
// server. Rolls the cache back on failure. Returns the server-returned
// skills object on success.
export async function chooseClass(classKey) {
  const before = getSkillState();
  if (before.classKey) return before;
  const optimistic = { classKey, pointsSpent: [] };
  saveState(optimistic);
  try {
    const res = await chooseSkillClassApi(classKey);
    const next = normalizeSkillState(res?.skills || optimistic);
    saveState(next);
    return next;
  } catch (err) {
    saveState(before);
    throw err;
  }
}

export async function spendPoint(nodeKey) {
  const before = getSkillState();
  if (before.pointsSpent.includes(nodeKey)) return before;
  const optimistic = { ...before, pointsSpent: [...before.pointsSpent, nodeKey] };
  saveState(optimistic);
  try {
    const res = await spendSkillPointApi(nodeKey);
    const next = normalizeSkillState(res?.skills || optimistic);
    saveState(next);
    return next;
  } catch (err) {
    saveState(before);
    throw err;
  }
}

// Paid respec. Charges gold server-side, hands every spent point back
// and clears the class so the picker shows again. No optimistic write:
// gold is on the line, so the local cache only moves once the server
// has committed the charge. Throws ApiError (402 when the player
// cannot afford it, 400 when there is nothing to reset) and leaves the
// cache untouched on failure.
export async function purchaseSkillReset() {
  const res = await purchaseSkillResetServer();
  const skills = normalizeSkillState(res?.skills);
  saveState(skills);
  return { skills, coins: res?.coins };
}
